import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import InlineGroup from 'components/Common/InlineGroup'
import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'

import Image from 'components/Common/Image'
import TextLink from 'components/Luxkit/TextLink'
import CurrencyContext from 'contexts/currencyContext'
import { getBNPLMethodAvailability } from 'lib/paymentUtils'
import useToggle from 'hooks/useToggle'
import InterestFreeLearnMoreModal from 'components/Common/Modals/InterestFreeLearnMoreModal/InterestFreeLearnMoreModal'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'

const Root = styled(Pane)`
  text-align: center;
`

const Logos = styled(InlineGroup)`
  margin-top: ${rem(16)};

  > * + * {
    margin-left: ${rem(16)};

    ${mediaQueryUp.tablet} {
      margin-left: ${rem(8)};
    }
  }
`

const LogoImages = styled(Image)`
    width: 100%;
    max-width: ${rem(130)};
    max-height: ${rem(40)};

    ${mediaQueryUp.tablet} {
      max-width: ${rem(90)};
    }
`

interface Props {
  className?: string;
  onLearnMore?: () => void;
  prices: Array<number> | number;
}

function OfferBNPLAd(props: Props) {
  const {
    className,
    prices,
    onLearnMore,
  } = props

  const [modalOpened, onInterestFreeModalClose, showInterestFreeModal] = useToggle(false)
  const currency = useContext(CurrencyContext)

  const handleOnLearnMore = useCallback(() => {
    showInterestFreeModal()
    onLearnMore?.()
  }, [onLearnMore, showInterestFreeModal])

  const bnplMethods = useMemo(() => {
    const allPrices = Array.isArray(prices) ? prices : [prices]
    return getBNPLMethodAvailability(currency, ...allPrices)
  }, [prices, currency])
  const hasAnyMethods = useMemo(() => Object.values(bnplMethods).some(Boolean), [bnplMethods])

  if (!hasAnyMethods) {
    return null
  }

  return (<>
    <InterestFreeLearnMoreModal
      isOpen={modalOpened}
      onClose={onInterestFreeModalClose}
    />
    <Root className={className}>
      <PaneBody>
        <BodyText variant="small" weight="semi-bold">
          Interest-free payment options available&nbsp;
          <TextLink onClick={handleOnLearnMore}>
            Learn more
          </TextLink>
        </BodyText>
        <Logos horizontal="center" vertical="center">
          {bnplMethods.isKlarnaAvailable || bnplMethods.isKlarnaBpAvailable && (
            <LogoImages id="ph5b1cik9gdapegknwl" alt="Klarna Pay" />
          )}
          {bnplMethods.isLatitudePayEnabled && (
            <LogoImages
              id="Latitude_Logo_Horiz_Blue_RGB_jkccmf"
              alt="Latitude Pay"
            />
          )}
          {bnplMethods.isAfterPayBpAvailable && (
            <LogoImages id="2kac81byj4yt5abm0nr" alt="After Pay" />
          )}
          {bnplMethods.isHoolahBpAvailable && (
            <LogoImages id="shopback-logo" alt="Shopback" />
          )}
          {bnplMethods.isAtomeBpAvailable && (
            <LogoImages id="atome-logo" alt="Atome" />
          )}
          {bnplMethods.isWaaveBpAvailable && (
            <LogoImages id="luxpay-logo" alt="LuxPay" />
          )}
        </Logos>
      </PaneBody>
    </Root>
  </>

  )
}

export default React.memo(OfferBNPLAd)
